(ns ui.completion.model
  "Paths and pure state transformers for the completion waiter.

   Two pieces of state, both plain data in app-db so they are visible in
   re-frame-10x and so every transition stays a pure function:

   - the *log* of completions that have already arrived, keyed by completion id
   - the *pending* waits, keyed by a locally generated watch id

   The log exists because a completion push and the reply that carries its id
   travel over different channels (a websocket push vs. the request's own
   reply), so their arrival order is genuinely not guaranteed. A waiter checks
   the log before registering, exactly as `wait-for-session-state!` evaluates
   its `ready?` before adding a watch."
  (:require
   [common.completion :as completion]))

(def root-path [::state])
(def log-path (conj root-path :log))
(def log-order-path (conj root-path :log-order))
(def pending-path (conj root-path :pending))
(def listeners-path (conj root-path :listeners))

(def max-logged-completions
  "How many arrived completions to remember.

   Bounded because completions arrive for work nobody is waiting on (a second
   tab, a wait that already timed out), and an unbounded log would grow for the
   lifetime of the session. The only thing the log has to survive is the gap
   between a push arriving and the reply carrying its id being processed -
   milliseconds - so a small window is ample."
  100)

(defn logged-completion
  [db completion-id]
  (get-in db (conj log-path completion-id)))

(defn record-completion
  "Add `payload` to the log, dropping the oldest entries past the cap.

   Insertion order is tracked separately rather than relying on map ordering,
   which ClojureScript maps do not promise once they grow past the array-map
   threshold."
  [db payload]
  (let [completion-id (:completion/id payload)]
    (if-not (string? completion-id)
      db
      (let [previous-order (get-in db log-order-path [])
            order (-> (->> previous-order
                           (remove (fn [logged-id] (= logged-id completion-id)))
                           vec)
                      (conj completion-id))
            overflow (max 0 (- (count order) max-logged-completions))
            dropped (subvec order 0 overflow)
            kept (subvec order overflow)]
        (-> db
            (assoc-in log-order-path kept)
            (update-in log-path
                       (fn [log]
                         (-> (apply dissoc log dropped)
                             (assoc completion-id payload)))))))))

(defn pending-wait
  [db watch-id]
  (get-in db (conj pending-path watch-id)))

(defn put-pending
  [db watch-id wait]
  (assoc-in db (conj pending-path watch-id) wait))

(defn take-pending
  "Remove and return `[db wait]` for `watch-id`, or `[db nil]` if it is already
   gone.

   The single-terminal-dispatch guard: whichever of the completion and the
   deadline gets here first takes the wait, and the loser finds `nil` and
   dispatches nothing. The pure equivalent of the `compare-and-set!` race guard
   in `fx.sente`'s `::send`."
  [db watch-id]
  (let [wait (pending-wait db watch-id)]
    [(update-in db pending-path dissoc watch-id) wait]))

(defn pending-watch-ids-for
  "Watch ids waiting on `completion-id`.

   A collection rather than a single id: nothing stops two parts of the UI
   waiting on the same completion, and both should be released."
  [db completion-id]
  (->> (get-in db pending-path)
       (keep (fn [[watch-id wait]]
               (when (= completion-id (:completion-id wait))
                 watch-id)))
       vec))

(defn continuation
  "The event vector to dispatch for `wait` given how it ended.

   `:on-timeout` and `:on-error` both fall back to `:on-complete` on purpose.
   Every consumer so far waits in order to *refetch* something, and refetching
   after a failed or lost completion is exactly the pre-existing behaviour -
   possibly too early, never wrong. Hanging, by contrast, would be a new
   failure mode; a waiter with no deadline was already a bug in this codebase."
  [wait outcome]
  (let [{:keys [on-complete on-timeout on-error]} wait]
    (case outcome
      :timeout (or on-timeout on-complete)
      :error (or on-error on-complete)
      on-complete)))

(defn outcome-for-status
  [status]
  (if (= status completion/error-status)
    :error
    :complete))

(defn put-listener
  "Register a standing listener, keyed by `listener-id`. A wait is for one
   completion that a request announced. A listener is for every completion of a
   `kind`, for a screen that must follow work that other parts of the UI start,
   for example a listing that shows the entities that a curtain changes. The
   listener stays until `remove-listener`. Registering again with the same id
   replaces the listener.

   `on-completion` is an event vector. The payload of the completion is added to
   it as the last element. `kind` limits the listener to one kind of completion.
   Without it the listener gets all of them."
  [db {:keys [listener-id on-completion kind]}]
  (assoc-in db
            (conj listeners-path listener-id)
            {:on-completion on-completion
             :kind          kind}))

(defn remove-listener
  [db listener-id]
  (update-in db listeners-path dissoc listener-id))

(defn listener-events
  "The event vectors to dispatch for a completion that arrived: the
   `:on-completion` event of each listener that wants its kind, with the payload
   added at the end."
  [db payload]
  (->> (vals (get-in db listeners-path))
       (filter (fn [{:keys [kind]}]
                 (or (nil? kind)
                     (= kind (:completion/kind payload)))))
       (mapv (fn [{:keys [on-completion]}]
               (conj on-completion payload)))))
